/** App-wide state: the signed-in user, the card catalog, meta, toasts, and the global socket notifications. */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Card, Catalog, ServerMessage } from '@gooflord/shared';
import { useNavigate } from 'react-router';
import { get, post, setCsrf, setUnauthorizedHandler } from './lib/api';
import { socket, type SocketStatus } from './lib/ws';

export interface User {
  id: string; username: string; display_name: string; role: string; avatar: string; bio: string; xp: number; rank: string; wins: number; losses: number; frame: string; title: string;
  gooflord: string; card_back: string; rating: number; email: string | null; email_verified: boolean; credits: number; invite_code: string; deck: { fighters?: string[]; items?: string[] };
  free_packs: number; free_pack_at: string; free_pack_last?: string | null; settings: { reduced_motion?: boolean; sound?: boolean; haptics?: boolean; turn_emails?: boolean }; guardian_ack: boolean; created_at: string;
  /** staff only: until 2FA unlocks this session, `role` reads 'user' */
  staff?: { role: string; totp_enabled: boolean; unlocked_until: string | null } | null;
}
export interface Meta {
  econ: Record<string, number>; emotes: { id: string; emoji: string; label: string }[]; deck_rules: { fightersMin: number; fightersMax: number; itemsMin: number; itemsMax: number; maxCopies: number; corruptedMax: number };
  modes: Record<string, { players: number; winLevel: number; turnLimit: number }>; beta_gate: boolean; coming_soon?: boolean; turn_seconds: number; disconnect_grace_seconds?: number; pack_odds: { per_card: Record<string, number>; per_pack?: Record<string, Record<string, number>>; guaranteed_rare_or_better: Record<string, number> };
  auth_mode: string; supabase_url: string | null;
}
export interface Toast { id: number; text: string; href?: string; kind?: 'info' | 'error' | 'success'; action?: { label: string; onClick: () => void } }

interface Store {
  user: User | null; setUser: (u: User | null) => void; refreshMe: () => Promise<User | null>; logout: () => Promise<void>;
  cards: Card[]; cat: Catalog; reloadCards: () => Promise<void>; meta: Meta | null; booted: boolean;
  /** boot couldn't reach the server (distinct from "not signed in") */ bootError: string | null; retryBoot: () => void;
  /** set when the server dropped our session; Landing shows a notice */ sessionExpired: boolean;
  toasts: Toast[]; notify: (text: string, opts?: Partial<Toast>) => void; dismiss: (id: number) => void;
  socketStatus: SocketStatus; challenge: { from: { display_name: string; avatar: string }; matchId: string } | null; clearChallenge: () => void;
}
const Ctx = createContext<Store>(null!);
export const useStore = () => useContext(Ctx);

export function StoreProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [booted, setBooted] = useState(false);
  const [bootError, setBootError] = useState<string | null>(null);
  const [bootTry, setBootTry] = useState(0);
  const [sessionExpired, setSessionExpired] = useState(false);
  const nav = useNavigate();
  const userRef = useRef<User | null>(null); userRef.current = user;
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [socketStatus, setSocketStatus] = useState<SocketStatus>('idle');
  const [challenge, setChallenge] = useState<Store['challenge']>(null);
  const nextId = useRef(1);

  const notify = useCallback((text: string, opts: Partial<Toast> = {}) => {
    const id = nextId.current++;
    setToasts((t) => [...t.slice(-3), { id, text, ...opts }]);
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), opts.kind === 'error' ? 5000 : 3600);
  }, []);
  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  const refreshMe = useCallback(async () => {
    const me = await get<{ user: User | null; csrf: string | null }>('/api/me');
    setCsrf(me.csrf); setUser(me.user); return me.user;
  }, []);
  const reloadCards = useCallback(async () => { const c = await get<{ cards: Card[] }>('/api/cards'); setCards(c.cards); }, []);
  const logout = useCallback(async () => { await post('/api/auth/logout').catch(() => {}); setUser(null); setCsrf(null); socket.disconnect(); }, []);

  // boot: a failed request is "couldn't load" (retry), not "signed out"; a user from /api/me is kept either way
  useEffect(() => {
    let live = true;
    (async () => {
      const rs = await Promise.allSettled([refreshMe(), reloadCards(), get<Meta>('/api/meta').then(setMeta)]);
      if (!live) return;
      const bad = rs.find((x): x is PromiseRejectedResult => x.status === 'rejected');
      setBootError(bad ? (bad.reason as Error)?.message || "Couldn't reach the server" : null);
      setBooted(true);
    })();
    return () => { live = false; };
  }, [refreshMe, reloadCards, bootTry]);
  const retryBoot = useCallback(() => { setBooted(false); setBootError(null); setBootTry((n) => n + 1); }, []);

  // session expired (API 401 or socket closed with 4401): sign out locally and send them to sign-in
  const expire = useCallback(() => {
    if (!userRef.current) return;
    userRef.current = null; setUser(null); setCsrf(null); socket.disconnect(); setSessionExpired(true); nav('/', { replace: true });
  }, [nav]);
  useEffect(() => { setUnauthorizedHandler(expire); const off = socket.onStatus((st) => { if (st === 'expired') expire(); }); return () => { setUnauthorizedHandler(null); off(); }; }, [expire]);
  useEffect(() => { if (user) setSessionExpired(false); }, [user]);

  useEffect(() => {
    if (!user) { socket.disconnect(); return; }
    socket.connect();
    const off = socket.on((m: ServerMessage) => {
      if (m.type === 'notify') notify(m.text, { href: m.href });
      if (m.type === 'challenge') setChallenge({ from: m.from, matchId: m.matchId });
      if (m.type === 'match_found' && !location.pathname.startsWith('/battle/')) notify('Match found!', { href: `/battle/${m.matchId}`, kind: 'success' });
    });
    const offS = socket.onStatus(setSocketStatus);
    post('/api/track', { name: 'session', props: { path: location.pathname } }).catch(() => {});
    return () => { off(); offS(); };
  }, [user?.id, notify]); // eslint-disable-line react-hooks/exhaustive-deps

  const cat = useMemo(() => Object.fromEntries(cards.map((c) => [c.slug, c])) as Catalog, [cards]);
  const value = useMemo<Store>(() => ({ user, setUser, refreshMe, logout, cards, cat, reloadCards, meta, booted, bootError, retryBoot, sessionExpired, toasts, notify, dismiss, socketStatus, challenge, clearChallenge: () => setChallenge(null) }),
    [user, refreshMe, logout, cards, cat, reloadCards, meta, booted, bootError, retryBoot, sessionExpired, toasts, notify, dismiss, socketStatus, challenge]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
